import { useState } from "react";
import { useOverlays } from "contexts/OverlayContext";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useLastReconciliationForAccount } from "@api/reconciliation.api";
import { getDisconnectedReconcileConnection } from "@helpers/financialAccount";
import { ReconciliationWorkflow } from "./ReconciliationWorkflow";
import { DisconnectedReconcileWarning } from "./DisconnectedReconcileWarning";

export const ReconciliationModalWrapper = () => {
    const { getOverlayInfo, closeOverlay, overlayIsClosing } = useOverlays();
    const { hasAccess, isFetched } = useAllowFeature(
        PREMIUM_FEATURES.RECONCILIATION,
    );
    const overlayInfo = getOverlayInfo("reconcilieFinancialAccount");
    const reconciliedFinancialAccountId = overlayInfo?.data?.financialAccountId;
    const overlayPreviousReconciliation =
        overlayInfo?.data?.previousReconciliation;
    const onSuccess = overlayInfo?.data?.onSuccess;
    const financialAccounts = useFinancialAccounts();
    const [
        disconnectAcknowledgedInstanceId,
        setDisconnectAcknowledgedInstanceId,
    ] = useState<number>();

    const reconciliedFinancialAccount = financialAccounts.find(
        (fa) => fa.id === reconciliedFinancialAccountId,
    );

    const { lastReconciliation: fetchedLastReconciliation, isLoading } =
        useLastReconciliationForAccount(
            reconciliedFinancialAccountId,
            reconciliedFinancialAccount?.entityId,
        );

    if (!reconciliedFinancialAccountId || !isFetched || !hasAccess) {
        return null;
    }

    if (!reconciliedFinancialAccount) {
        throw new Error("Financial account not found");
    }

    const disconnected = getDisconnectedReconcileConnection(
        reconciliedFinancialAccount,
    );

    if (
        disconnected &&
        disconnectAcknowledgedInstanceId !== overlayInfo?.instanceId
    ) {
        return (
            <DisconnectedReconcileWarning
                connection={disconnected.connection}
                integrationAccount={disconnected.integrationAccount}
                show={!overlayIsClosing}
                onContinue={() =>
                    setDisconnectAcknowledgedInstanceId(overlayInfo?.instanceId)
                }
                onCancel={closeOverlay}
            />
        );
    }

    const previousReconciliation =
        overlayPreviousReconciliation ?? fetchedLastReconciliation;
    const isLoadingLastReconciliation =
        !overlayPreviousReconciliation && isLoading;

    return (
        <ReconciliationWorkflow
            show={!overlayIsClosing}
            reconciliedFinancialAccount={reconciliedFinancialAccount}
            previousReconciliation={previousReconciliation}
            isLoadingLastReconciliation={isLoadingLastReconciliation}
            onHide={closeOverlay}
            onSuccess={onSuccess}
        />
    );
};
